import type { FC, ReactNode } from 'react';
import React, { Fragment, useState } from 'react';
import { useIntl } from 'react-intl';

import MenuGroup from '@atlaskit/menu/menu-group';

import {
	VIEW_PAGE,
	WHITEBOARD_BOARD,
	DATABASE_VIEW,
	SLIDES_VIEW,
	EMBED_VIEW,
	FOLDER_VIEW,
} from '@confluence/named-routes/entry-points/routes.ts';
import { useIsSuperAdmin } from '@confluence/super-admin/entry-points/AdminKeyQuery.ts';
import type { HoverPageCardProps } from '@confluence/page-card/entry-points/useHoverPageCard.ts';

import { SpaceViewsListItem } from './SpaceViewsListItem.tsx';

export type SpaceViewsPage = {
	title?: string | null;
	id?: string | null;
	hasRestrictions?: boolean;
	hasInheritedRestrictions?: boolean;
	displayEmoji?: string;
	type?: string;
	subType?: string;
};

type SpaceViewsListProps = {
	pagesList: Array<SpaceViewsPage>;
	onItemClick?: (id: string, index?: number) => void;
	spaceKey?: string;
	contentId?: string;
	shouldRenderAfterIcon?: boolean;
	renderTitle?: ({ title }: { title: string }) => ReactNode;
} & Partial<HoverPageCardProps>;

const getUrlByType = (type) => {
	const typeToUrl = {
		page: VIEW_PAGE,
		whiteboard: WHITEBOARD_BOARD,
		database: DATABASE_VIEW,
		slide: SLIDES_VIEW,
		embed: EMBED_VIEW,
		folder: FOLDER_VIEW,
	};

	return type && typeToUrl[type] ? typeToUrl[type] : VIEW_PAGE;
};

export const SpaceViewsList: FC<SpaceViewsListProps> = ({
	pagesList,
	onItemClick,
	spaceKey,
	contentId,
	closeAllHoverPageCards = () => {},
	onEnterHoverTarget = () => {},
	onLeaveHoverTarget = () => {},
	onBlurHoverTarget = () => {},
	shouldRenderAfterIcon,
	renderTitle = ({ title }) => title,
}) => {
	const intl = useIntl();
	const { isSuperAdmin } = useIsSuperAdmin();
	const [openQuickActionsId, setOpenQuickActionsId] = useState<string | null>(null);
	const [focusedQuickActionsId, setFocusedQuickActionsId] = useState<string | null>(null);
	return (
		<Fragment>
			<MenuGroup role="list">
				{pagesList.map(
					(
						{ title, id, hasRestrictions, hasInheritedRestrictions, displayEmoji, type, subType },
						index,
					) => {
						const isCurrentPage = Boolean(contentId && contentId === id);
						let parsedId = id;
						// databases, slides, embeds, and folders currently have their id returned as 'activation/ACTIVATION_ID/ENTITY_ID', so we are parsing the entity id
						if (
							(type === 'database' || type === 'embed' || type === 'slide' || type === 'folder') &&
							id
						) {
							parsedId = id.split('/').pop() || null;
						}
						const contentLink =
							parsedId && spaceKey
								? getUrlByType(type).toUrl({
										spaceKey,
										contentId: parsedId,
									})
								: '';

						const item = {
							id: parsedId || '',
							hasRestrictions,
							hasInheritedRestrictions,
							data: {
								type,
								subType,
							},
						};
						return (
							<SpaceViewsListItem
								intl={intl}
								url={contentLink}
								title={title || ' '}
								displayEmoji={displayEmoji}
								contentType={type}
								onItemClick={onItemClick}
								key={id}
								index={index}
								isSelected={isCurrentPage}
								spaceKey={spaceKey || ''}
								item={item}
								closeAllHoverPageCards={closeAllHoverPageCards}
								isSuperAdmin={isSuperAdmin}
								isQuickActionsOpen={openQuickActionsId === item.id}
								setOpenQuickActionsId={setOpenQuickActionsId}
								isQuickActionsFocused={focusedQuickActionsId === item.id}
								setFocusedQuickActionsId={setFocusedQuickActionsId}
								setIsEditingTitleId={noop}
								onEnterHoverTarget={onEnterHoverTarget}
								onLeaveHoverTarget={onLeaveHoverTarget}
								onBlurHoverTarget={onBlurHoverTarget as (e) => void}
								shouldRenderAfterIcon={shouldRenderAfterIcon}
								renderTitle={renderTitle}
							/>
						);
					},
				)}
			</MenuGroup>
		</Fragment>
	);
};

const noop = () => Promise.resolve();
